import Handlebars from 'handlebars';

type TemplateContent = {
  html: string;
  css: string;
  js?: {
    slug: string;
    content: string | null;
  } | undefined;
  json?: string | null;
  compile?: boolean;
};

// List of trusted template slugs that are allowed to register helpers
const TRUSTED_TEMPLATE_SLUGS = [
  'global-js', // @todo: replace THIS WITH category-global-js (bankruptcy-global-js)
];

// Track registered helpers to avoid duplicate registrations
const registeredHelpers = new Set<string>();

/**
 * Registers Handlebars helpers from JavaScript content
 */
const registerHandlebarsHelpers = (jsContent: { slug: string; content: string | null }): boolean => {
  const { slug, content: jsHelpers } = jsContent;

  // Skip if no slug or not in trusted list, to prevent any malicious code execution
  if (!slug || !TRUSTED_TEMPLATE_SLUGS.includes(slug)) {
    console.warn(`Skipping helper registration for untrusted template: ${slug}`);
    return false;
  }

  // Skip if helpers for this slug are already registered
  // @todo this wouldn't work, when in the editor mode but need to revisit this
  if (registeredHelpers.has(slug)) {
    return true;
  }
  try {
  // Clear existing helpers to avoid duplicates
    Handlebars.registerHelper({});

    if (jsHelpers) {
      try {
        /*
          Create a function from the JS content and execute it with Handlebars
        */

        // eslint-disable-next-line no-new-func
        const registerHelpers = new Function('Handlebars', `
          try {
            ${jsHelpers}
            return true;
          } catch (e) {
            console.error('Error in Handlebars helpers:', e);
            return false;
          }
        `);

        const success = registerHelpers(Handlebars) !== false;
        if (success) {
          registeredHelpers.add(slug);
        }
        return success;
      } catch (error) {
        console.error('Error registering Handlebars helpers:', error);
        return false;
      }
    }
    return true;
  } catch (error) {
    console.error('Error in registerHandlebarsHelpers:', error);
    return false;
  }
};

/**
 * Compiles Handlebars template with data
 */
const compileTemplate = (
  html: string,
  json?: string | null,
  js?: {
    slug: string;
    content: string | null;
  } | undefined,
): string => {
  try {
    // Register helpers from the JS content if provided
    if (js && js.content) {
      const success = registerHandlebarsHelpers(js);
      if (!success) {
        console.warn(`Skipping helper registration for template: ${js.slug}`);
        // Continue execution without helpers rather than showing an error
      }
    }

    // Compile the template
    const template = Handlebars.compile(html);

    // Parse the JSON data if provided, otherwise use empty object
    const data = json ? JSON.parse(json) : {};

    // Execute the template with data
    let processedHtml = html;
    if (json) {
      try {
        processedHtml = template(data);
      } catch (error) {
        console.error('Error compiling template:', error);
        return `<div style="color: red; padding: 20px;">Error compiling template: ${error instanceof Error ? error.message : 'Unknown error'}</div>`;
      }
    }

    return processedHtml;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error compiling template:', error);
    return `<div style="color: red; padding: 20px;">Error compiling template: ${errorMessage}</div>`;
  }
};

/**
 * Combines all template parts into a complete HTML document
 */
const getCombinedTemplateContent = ({
  html,
  css = '',
  js,
  json,
  compile = false,
}: TemplateContent): string => {
  let processedHtml = html;
  if (compile && json) {
    try {
      processedHtml = compileTemplate(html, json, js);
    } catch (error) {
      console.error('Error compiling template:', error);
      return `
        <style>${css}</style>
        <div style="color: red; padding: 20px;">
          Error compiling template: ${error instanceof Error ? error.message : 'Unknown error'}
        </div>
      `;
    }
  }

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>${css}</style>
      <script>
        window.sampleData = ${json || '{}'};
      </script>
    </head>
    <body>
      ${processedHtml}
      ${js ? `<script>${js}</script>` : ''}
    </body>
    </html>
  `;
};

const handleBarsTemplateUtils = {
  compileTemplate,
  getCombinedTemplateContent,
  registerHandlebarsHelpers,
};

export default handleBarsTemplateUtils;
